import { useMutation, useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/apiClient";
import type {
  DraftCalendarItem,
  DraftMeetingRequest,
  DraftMeetingResponse,
  DraftMeetingHistoryItem,
  DraftCalendarComment,
  TrackUserCommentsRequest,
  FiscalYearResponse,
  CalendarSubmissionResponse,
  UpdateShowInGRCRequest,
  BulkUpdateStatusRequest,
  UpdateDraftStatesRequest,
  UpdateSignOffActionRequest,
  BulkDraftPushToSummitRequest,
  BulkActionInValidStatusListResponse,
  RequestSignOffResponse,
  SubmitSignOffRequest,
  CalendarEvent,
  CalendarHoliday,
  CalendarDayMeeting,
  CheckDraftBusinessRequest,
  CheckUserBusinessMapResponse,
  CloseDraftCalendarRequest
} from "@/types/draftCalendar";

// ─── Query keys ────────────────────────────────────────────────────────────────

export const draftCalendarKeys = {
  all: () => ["draft-calendar"] as const,
  fiscalYears: () => ["draft-calendar", "fiscal-years"] as const,
  list: (fiscalYearId: number, userEmail: string) =>
    ["draft-calendar", "list", fiscalYearId, userEmail] as const,
  meeting: (id: number) => ["draft-calendar", "meeting", id] as const,
  calendarStatus: (fiscalYearId: number) =>
    ["draft-calendar", "calendar-status", fiscalYearId] as const,
  attendeeLevels: () => ["draft-calendar", "attendee-levels"] as const,
  locations: () => ["draft-calendar", "locations"] as const,
  meetingTypes: () => ["draft-calendar", "meeting-types"] as const,
  businesses: () => ["draft-calendar", "businesses"] as const,
  duDeclinedReasons: () => ["draft-calendar", "du-declined-reasons"] as const,
  calendarEvents: (month: number, year: number, userEmail: string, meetingName: string) =>
    ["draft-calendar", "calendar-events", month, year, userEmail, meetingName] as const,
  importantDates: (month: number, year: number) =>
    ["draft-calendar", "important-dates", month, year] as const,
  meetingHistory: (id: number) => ["draft-calendar", "meeting-history", id] as const

};

// ─── Reference data ────────────────────────────────────────────────────────────

export function useFiscalYears() {
  return useQuery({
    queryKey: draftCalendarKeys.fiscalYears(),
    queryFn: () => apiGet<FiscalYearResponse[]>("DraftCalendar/GetAllFiscalYears"),
    staleTime: 5 * 60_000
  });
}

export function useDraftCalendar(fiscalYearId: number, userEmail: string) {
  return useQuery({
    queryKey: draftCalendarKeys.list(fiscalYearId, userEmail),
    queryFn: async () => {
      const qs = new URLSearchParams({
        fiscalYearId: String(fiscalYearId),
        userEmail,
      });
      const data = await apiGet<(DraftCalendarItem & { showinGRC?: boolean })[]>(`DraftCalendar?${qs}`);
      // Normalize API casing: "showinGRC" → "showInGRC"
      return data.map((item) => ({
        ...item,
        showInGRC: item.showInGRC ?? item.showinGRC ?? false,
      })) as DraftCalendarItem[];
    },
    enabled: fiscalYearId > 0 && userEmail.length > 0,
    staleTime: 30_000,
    refetchOnMount: "always"
  });
}

export function useDraftMeeting(id: number) {
  return useQuery({
    queryKey: draftCalendarKeys.meeting(id),
    queryFn: () =>
      apiGet<DraftMeetingResponse>(`DraftCalendar/GetDraftMeetingById/${id}`),
    enabled: id > 0
  });
}

export function useDraftMeetingHistory(id: number, enabled = true) {
  return useQuery({
    queryKey: draftCalendarKeys.meetingHistory(id),
    queryFn: () =>
      apiGet<DraftMeetingHistoryItem[]>(`SoftHolds/GetSoftholdsHistoryById/${id}`),
    enabled: enabled && id > 0,
    staleTime: 0
  });
}

export function useCalendarSubmissionStatus(fiscalYearId: number) {
  return useQuery({
    queryKey: draftCalendarKeys.calendarStatus(fiscalYearId),
    queryFn: () =>
      apiGet<CalendarSubmissionResponse>(
        `DraftCalendar/CheckCalendarSubmission/${fiscalYearId}`
      ),
    enabled: fiscalYearId > 0,
    staleTime: 30_000
  });
}

/** Business → [BusinessLine] map */
export function useDraftBusinesses() {
  return useQuery({
    queryKey: draftCalendarKeys.businesses(),
    queryFn: () =>
      apiGet<Record<string, string[]>>("Business/GetBusinessAndBusinessLineTypes"),
    staleTime: 5 * 60_000
  });
}

/** Business → MeetingType → [Category] map (key = business, value = { meetingType: [categories] }) */
export function useDraftMeetingTypes() {
  return useQuery({
    queryKey: draftCalendarKeys.meetingTypes(),
    queryFn: () =>
      apiGet<Record<string, Record<string, string[]>>>("MeetingType/GetMeetingTypesWithCategories"),
    staleTime: 5 * 60_000
  });
}

export function useAttendeeLevels() {
  return useQuery({
    queryKey: draftCalendarKeys.attendeeLevels(),
    queryFn: () => apiGet<string[]>("AttendeeLevel/GetAllAttendeeLevels"),
    staleTime: 5 * 60_000
  });
}

export function useLocations() {
  return useQuery({
    queryKey: draftCalendarKeys.locations(),
    queryFn: () => apiGet<string[]>("Locations/GetAllLocations"),
    staleTime: 5 * 60_000
  });
}

export function useDUDeclinedReasons(enabled = true) {
  return useQuery({
    queryKey: draftCalendarKeys.duDeclinedReasons(),
    queryFn: () => apiGet<string[]>("DraftCalendar/GetDUDeclinedReasons"),
    enabled,
    staleTime: 5 * 60_000
  });
}

export function useCalendarEvents(month: number, year: number, userEmail: string, meetingName = "") {
  return useQuery({
    queryKey: draftCalendarKeys.calendarEvents(month, year, userEmail, meetingName),
    queryFn: async () => {
      const events = await apiGet<CalendarEvent[]>(
        `SoftHolds/GetAllEventsForCalendarView/${month}/${year}/${encodeURIComponent(userEmail)}`
      );
      if (!meetingName.trim()) return events ?? [];
      const filter = meetingName.trim().toLowerCase();
      return (events ?? []).filter((event) => event.meetingName?.toLowerCase() === filter);
    },
    enabled: month >= 1 && month <= 12 && year > 0 && userEmail.length > 0,
    staleTime: 30_000
  });
}

export function useCalendarHolidays(month: number, year: number) {
  return useQuery({
    queryKey: draftCalendarKeys.importantDates(month, year),
    queryFn: async () => {
      try {
        return await apiGet<CalendarHoliday[]>(`ImportantDates/GetAllImportantDatesByDateRange/${month}/${year}`);
      } catch (error) {
        if (error instanceof Error && error.message.includes("failed (404)")) {
          return [];
        }
        throw error;
      }
    },
    enabled: month >= 1 && month <= 12 && year > 0,
    staleTime: 5 * 60_000
  });
}

export function useDraftCalendarDayMeetings() {
  return useMutation({
    mutationFn: (draftIds: number[]) => {
      if (draftIds.length === 0) return Promise.resolve([] as CalendarDayMeeting[]);
      const qs = new URLSearchParams({ commaSeparatedIds: draftIds.join(",") });
      return apiGet<CalendarDayMeeting[]>(`DraftCalendar/GetDraftMeetingsByIds?${qs}`);
    }
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useSaveDraftMeeting() {
  return useMutation({
    mutationFn: (body: DraftMeetingRequest) =>
      apiPost<DraftMeetingRequest, boolean>("DraftCalendar/SaveDraftEvent",body)

  });
}

export function useUpdateShowInGRC() {
  return useMutation({
    mutationFn: (body: UpdateShowInGRCRequest) =>
      apiPost<UpdateShowInGRCRequest, number[]>("SoftHolds/UpdateShowInGRC", body)
      
  });
}

export function useBulkUpdateStatus() {
  return useMutation({
    mutationFn: (body: BulkUpdateStatusRequest) =>
      apiPost<BulkUpdateStatusRequest, number>("DraftCalendar/BulkUpdateStatus", body)

  });
}

export function useBulkUpdateStates() {
  return useMutation({
    mutationFn: (body: UpdateDraftStatesRequest) =>
      apiPost<UpdateDraftStatesRequest, boolean>("DraftCalendar/BulkUpdateStates", body)
  });
}

export function useUpdateSignOffAction() {
  return useMutation({
    mutationFn: (body: UpdateSignOffActionRequest) =>
      apiPost<UpdateSignOffActionRequest, number>("DraftCalendar/UpdateSignOffAction",body)
      
  });
}

export function useBulkDraftPushToSummit() {
  return useMutation({
    mutationFn: (body: BulkDraftPushToSummitRequest) =>
      apiPost<BulkDraftPushToSummitRequest, BulkActionInValidStatusListResponse>("DraftCalendar/BulkDraftPushToSummit", body)
  });
}

export function useCheckBusinessEligibility(fiscalYearId: number, enabled = true) {
  return useQuery({
    queryKey: ["draft-calendar", "business-eligibility", fiscalYearId],
    queryFn: () =>
      apiGet<RequestSignOffResponse[]>(`DraftCalendar/GetBusinessesEligibleForSignOff/${fiscalYearId}`),
    enabled: enabled && fiscalYearId > 0,
    staleTime: 0
  });
}

export function useSubmitSignOffRequest() {
  return useMutation({
    mutationFn: (body: SubmitSignOffRequest) =>
      apiPost<SubmitSignOffRequest, boolean>("DraftCalendar/SubmitSignOffRequest", body)
  });
}

export function useAddDraftComment() {
  return useMutation({
    mutationFn: (body: DraftCalendarComment) =>
      apiPost<DraftCalendarComment, boolean>("DraftCalendar/AddComment", body)
  });
}

export function useCheckDraftBusiness() {
  return useMutation({
    mutationFn: (body: CheckDraftBusinessRequest) =>
      apiPost<CheckDraftBusinessRequest, CheckUserBusinessMapResponse[]>("DraftCalendar/CheckUserBusinessMapping", body)
  });
}

export function useCloseDraftCalendar() {
  return useMutation({
    mutationFn: (body: CloseDraftCalendarRequest) =>
      apiPost<CloseDraftCalendarRequest, boolean>("DraftCalendar/SubmitCloseCalendar", body)
  });
}

export function useUpdateDraftComment() {
  return useMutation({
    mutationFn: (body: DraftCalendarComment) =>
      apiPost<DraftCalendarComment, boolean>("DraftCalendar/EditComment", body)
  });
}

export function useTrackUserViewedComments() {
  return useMutation({
    mutationFn: (body: TrackUserCommentsRequest) =>
      apiPost<TrackUserCommentsRequest, boolean>("DraftCalendar/TrackUserViewedComments", body)
  });
}
